<script>
  import { billingEnumMixins } from '@/mixins/billingEnumMixins';
  import {
    addAffPaymentApp,
    AffPaymentAppAPI,
    financePayment,
    getAffPaymentAppPreviewData,
    getAffPaymentDetailByNumber,
    operationAuditAffPaymentAppStatus,
  } from 'api/affSendingBilling/application/affPaymentApplication';
  import { checkNumNA } from 'utils/number';
  import { isNotBlank, splitStr } from 'utils/string';
  import { userInfoMixin } from '@/mixins/userInfoMixin';
  import { validateAmount } from 'utils/validator';
  import { financeInfoMixins } from '@/mixins/financeInfoMixins';
  import { S3Api, uploadFile } from 'api/s3';

  const defaultFormData = {
    affPaymentAppNumber: null,
    recordDateRange: [],
    affCompanyPlatform: null,
    affEntity: null,
    hyperTapEntity: null,
    affHedgeAmount: null,
    applyPaymentAmount: null,
    paymentStatus: null,
    affBfbId: null,
    remark: null,
    paymentTime: null,
    operationAuditRemark: null,
    financeAuditRemark: null,
    currency: 'CNY',
    affRevenue: null,
    financePaymentAmount: null,
    affConfirmedAmount: null,
    uploadInfoItems: [],
    bankInfo: null,
    emailStatus: null,
    sendEmail: [],
  };

  const defaultPreviewData = {
    totalAffConfirmedAmount: null,
    totalAffHedgeAmount: null,
    applyPaymentAmount: null,
  };

  export default {
    name: 'AffPaymentAppBaseForm',
    computed: {
      S3Api() {
        return S3Api;
      },
      AffPaymentAppAPI() {
        return AffPaymentAppAPI;
      },
    },
    props: {
      dialogType: {
        type: String,
        default: 'new',
      },
      paymentType: {
        type: String,
        default: 'POST_PAID',
      },
      auditStatus: {
        type: String,
        default: '',
      },
      dialogVisible: {
        type: Boolean,
        default: false,
      },
    },
    mixins: [billingEnumMixins, userInfoMixin, financeInfoMixins],
    data() {
      return {
        formData: Object.assign({}, defaultFormData),
        previewWatchField: {
          recordDateRange: [],
          affCompanyPlatform: null,
          affEntity: null,
        },
        previewData: Object.assign({}, defaultPreviewData),
        checkMessage: '',
        fileList: [],
        rules: {
          affCompanyPlatform: [{ required: true, trigger: 'blur' }],
          affEntity: [{ required: true, trigger: 'blur' }],
          recordDateRange: [{ required: true, trigger: 'blur' }],
          hyperTapEntity: [{ required: true, trigger: 'blur' }],
          paymentStatus: [{ required: true, trigger: 'blur' }],
          currency: [{ required: true, trigger: 'blur' }],
          affPaymentAppNumber: [{ required: true, trigger: 'blur' }],
          paymentTime: [{ required: true, trigger: 'blur' }],
          financePaymentAmount: [{ validator: validateAmount, required: true, trigger: 'blur' }],
          remark: [],
          applyPaymentAmount: [],
          affConfirmedAmount: [],
          affHedgeAmount: [],
          operationAuditRemark: [],
          uploadInfoItems: [],
          financeAuditRemark: [],
          sendEmail: [],
        },
        bankInfoList: [],
        formProcessing: false,
      };
    },
    watch: {
      formData: {
        immediate: true,
        handler(val) {
          // 财务付款表单无需预览
          if (this.auditStatus === 'FINANCE_PAID') {
            return;
          }
          const { recordDateRange, affCompanyPlatform, affEntity } = val;
          // 值是否发生变化
          if (
            this.previewWatchField.affEntity === affEntity &&
            this.previewWatchField.affCompanyPlatform === affCompanyPlatform &&
            this.previewWatchField.recordDateRange.join(',') === recordDateRange.join(',')
          ) {
            return;
          }

          if (recordDateRange && recordDateRange.length > 0 && affCompanyPlatform && affEntity) {
            setTimeout(() => {
              if (
                recordDateRange &&
                recordDateRange.length > 0 &&
                affCompanyPlatform &&
                affEntity
              ) {
                this.handlePreview();
                this.previewWatchField = {
                  recordDateRange,
                  affCompanyPlatform,
                  affEntity,
                };
              }
            }, 500);
          }
        },
        deep: true,
      },
      paymentType: {
        handler(val) {
          if (val === 'PRE_PAID') {
            const recordDate = `${new Date().getFullYear()}-${new Date().getMonth() + 1}`;
            // 当前月份
            this.formData.recordDateRange = [recordDate, recordDate];
          } else {
            this.formData.recordDateRange = [];
          }
        },
      },
      formCompanyPlatformLists: {
        handler(newVal) {
          this.formCompanyPlatformList = Object.assign([], newVal);
        },
      },
      formCompanyEntityLists: {
        handler(newVal) {
          this.formCompanyEntityList = Object.assign([], newVal);
        },
      },
    },
    methods: {
      splitStr,
      isNotBlank,
      checkNumNA,
      handleFormProcessingEnd() {
        setTimeout(() => {
          this.formProcessing = false;
        }, 500);
      },
      handleGetMergeInfo() {
        if (!this.formData.affPaymentAppNumber) {
          return;
        }
        this.formProcessing = true;
        getAffPaymentDetailByNumber(this.formData.affPaymentAppNumber)
          .then((res) => {
            if (res.result) {
              this.formData = Object.assign(this.formData, res.result);
            }
          })
          .catch(() => {})
          .finally(() => {
            this.handleFormProcessingEnd();
          });
      },
      handleSubmit() {
        this.$refs.baseForm.validate((valid) => {
          if (valid) {
            this.formProcessing = true;
            switch (this.dialogType) {
              case 'new':
                addAffPaymentApp({ paymentType: this.paymentType, ...this.formData })
                  .then(() => {
                    this.$message({
                      message: 'Add Success',
                      type: 'success',
                    });
                    this.$emit('refresh-list');
                    this.handleClose();
                  })
                  .finally(() => {
                    this.handleFormProcessingEnd();
                  });
                break;
              case 'edit':
                break;
              case 'status':
                if (this.auditStatus === 'FINANCE_PAID') {
                  financePayment({
                    affPaymentAppNumber: this.formData.affPaymentAppNumber,
                    financePaymentAmount: this.formData.financePaymentAmount,
                    paymentStatus: this.formData.paymentStatus,
                    paymentTime: this.formData.paymentTime,
                    currency: this.formData.currency,
                    financeAuditRemark: this.formData.financeAuditRemark,
                    uploadInfoItems: this.formData.uploadInfoItems,
                  })
                    .then(() => {
                      this.$message({
                        message: 'Success',
                        type: 'success',
                      });
                      this.$emit('refresh-list');
                      this.handleClose();
                    })
                    .finally(() => {
                      this.handleFormProcessingEnd();
                    });
                } else {
                  operationAuditAffPaymentAppStatus({
                    affPaymentAppNumber: this.formData.affPaymentAppNumber,
                    operationAuditRemark: this.formData.operationAuditRemark,
                    financeAuditRemark: this.formData.financeAuditRemark,
                    auditStatus: this.auditStatus,
                  })
                    .then(() => {
                      this.$message({
                        message: 'Success',
                        type: 'success',
                      });
                      this.$emit('refresh-list');
                      this.handleClose();
                    })
                    .finally(() => {
                      this.handleFormProcessingEnd();
                    });
                }
                break;
            }
          } else {
            return false;
          }
        });
      },
      handleClose() {
        this.$emit('update:dialogVisible', false);
        this.$emit('update:auditStatus', 'null');
        this.$emit('update:paymentType', 'POST_PAID');
        this.$refs.baseForm.resetFields();
        this.formData = Object.assign({}, defaultFormData);
        this.previewData = Object.assign({}, defaultPreviewData);
        this.fileList = [];
        this.checkMessage = '';
        this.previewWatchField = {
          recordDateRange: [],
          affCompanyPlatform: null,
          affEntity: null,
        };
      },
      handlePreview() {
        if (
          this.formData.recordDateRange.length === 0 ||
          !this.formData.affCompanyPlatform ||
          !this.formData.affEntity
        ) {
          this.$message.error('请填写日期、公司平台、公司实体');
          return;
        }
        this.formProcessing = true;
        this.checkMessage = '';
        getAffPaymentAppPreviewData({
          recordDateRange: this.formData.recordDateRange,
          affCompanyPlatform: this.formData.affCompanyPlatform,
          affEntity: this.formData.affEntity,
          paymentType: this.paymentType,
        })
          .then((res) => {
            if (res && res.result) {
              this.previewData.totalAffConfirmedAmount = res.result.totalAffConfirmedAmount;
              this.previewData.totalAffHedgeAmount = res.result.totalAffHedgeAmount;
              this.previewData.applyPaymentAmount = res.result.applyPaymentAmount;
              this.checkMessage = res.result.checkMessage;
              if (res.result.bankInfoList) {
                this.bankInfoList = res.result.bankInfoList;
              }
            }
          })
          .catch(() => {})
          .finally(() => {
            setTimeout(() => {
              this.formProcessing = false;
            }, 500);
          });
      },
      handleUpload(param) {
        const fileObj = param.file;
        console.log(param);
        console.log(this.fileList);
        const form = new FormData();
        form.append('file', fileObj);
        try {
          uploadFile(form)
            .then((res) => {
              this.$message.success('上传成功');
              if (res && res.result) {
                this.formData.uploadInfoItems.push(res.result);
              }
              param.onSuccess(res);
            })
            .catch((err) => {
              console.log(err);
              param.onError(err);
            })
            .finally(() => {
              this.formProcessing = false;
            });
        } catch (err) {
          this.formProcessing = false;
          console.log(err);
          param.onError(err);
        }
      },
      doUpload() {
        this.$refs.upload.submit();
      },
      handleUploadSuccess(res) {
        try {
          if (!res) {
            this.$message.error('上传失败,请重试');
            return;
          }
          if (res.code && res.code === 400) {
            this.$message.error(res.message);
            return;
          }
          if (res.result) {
            this.formData.uploadInfoItems.push(res.result);
          }
          this.$message.success('上传成功');
        } finally {
          this.formProcessing = false;
        }
      },
      handleUploadError() {
        this.formProcessing = false;
        this.$message.error('上传失败');
      },
      handleBeforeUploadInvoice(file) {
        // 检查是否存在同名文件
        const hasSameNameFile = this.formData.uploadInfoItems.some(
          (item) => item.filename === file.name
        );
        if (hasSameNameFile) {
          this.$message.error('已存在同名文件，请重新上传');
          return false;
        }
      },
      handleRemoveInvoiceList(file) {
        // 删除 this.formData.uploadInfoItems 同名文件
        const index = this.formData.uploadInfoItems.findIndex(
          (item) => item.filename === file.name
        );
        if (index !== -1) {
          this.formData.uploadInfoItems.splice(index, 1);
        }
      },
      showPaymentStatus(paymentStatus) {
        const temp = this.affPaymentAppOperationPaymentStatusList.concat(
          this.affPrePaidPaymentAppPaymentStatusList
        );
        return this.getEnumLabel(temp, paymentStatus);
      },
    },
  };
</script>

<template>
  <el-dialog
    :title="
      dialogType === 'new' ? 'Aff Payment Management Create' : 'Aff Payment Management Update'
    "
    :visible="dialogVisible"
    :close-on-click-modal="false"
    width="40%"
    @close="handleClose"
  >
    <el-alert
      v-show="isNotBlank(this.checkMessage) && this.auditStatus !== 'FINANCE_PAID'"
      :title="checkMessage"
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom: 20px"
    />
    <el-form
      label-position="left"
      label-width="200px"
      ref="baseForm"
      :model="formData"
      :rules="rules"
      :disabled="formProcessing"
    >
      <!-- 运营审核表单-->
      <section
        v-if="
          dialogType === 'status' &&
            ['APPROVED', 'REJECTED', 'SENT_FINANCE'].includes(auditStatus) &&
            isAffAm()
        "
        class="grid-form-large"
      >
        <el-form-item label="Aff Payment App Number" prop="affPaymentAppNumber" required>
          <el-input v-model="formData.affPaymentAppNumber" placeholder="please input" />
        </el-form-item>
        <el-form-item
          v-if="auditStatus !== 'SENT_FINANCE'"
          label="Audit Remark "
          prop="operationAuditRemark"
        >
          <el-input
            v-model="formData.operationAuditRemark"
            type="textarea"
            show-word-limit
            maxlength="255"
            label-width="240px"
            placeholder="please input"
          />
        </el-form-item>
      </section>

      <section
        v-if="dialogType === 'status' && auditStatus === 'FINANCE_REJECTED' && isFinance()"
        class="grid-form-large"
      >
        <el-form-item label="Aff Payment App Number" prop="affPaymentAppNumber" required>
          <el-input v-model="formData.affPaymentAppNumber" placeholder="please input" />
        </el-form-item>
        <el-form-item label="Audit Remark " prop="operationAuditRemark">
          <el-input
            v-model="formData.financeAuditRemark"
            type="textarea"
            show-word-limit
            maxlength="255"
            label-width="240px"
            placeholder="please input"
          />
        </el-form-item>
      </section>

      <!--   财务付款表单   -->
      <section v-if="dialogType === 'status' && auditStatus === 'FINANCE_PAID'" class="grid-form">
        <el-form-item label="Aff Payment App Number" prop="affPaymentAppNumber">
          <el-input v-model="formData.affPaymentAppNumber">
            <template #append>
              <el-button
                icon="el-icon-search"
                :loading="formProcessing"
                @click="handleGetMergeInfo"
              />
            </template>
          </el-input>
        </el-form-item>
        <el-form-item label="Date">
          <el-date-picker
            disabled
            v-model="formData.recordDateRange"
            format="yyyy-MM"
            value-format="yyyy-MM"
            type="monthrange"
            align="right"
            unlink-panels
            style="width: 250px"
            range-separator="to"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
          />
        </el-form-item>
        <el-form-item label="Aff Company Platform">
          <el-input v-model="formData.affCompanyPlatform" disabled />
        </el-form-item>
        <el-form-item label="Aff Entity">
          <el-input v-model="formData.affEntity" disabled />
        </el-form-item>
        <el-form-item label="HyperTap Entity">
          <el-select v-model="formData.hyperTapEntity" filterable disabled>
            <el-option
              v-for="item in hyperTapCompanyEntities"
              :key="item.bfiId"
              :label="item.companyEntityAlias"
              :value="item.companyEntity"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Aff Confirmed Amount">
          <el-input v-model="formData.affConfirmedAmount" disabled />
        </el-form-item>
        <el-form-item label="Aff Hedge Amount">
          <el-input v-model="formData.affHedgeAmount" disabled />
        </el-form-item>
        <el-form-item label="Apply Payment Amount">
          <el-input v-model="formData.applyPaymentAmount" disabled />
        </el-form-item>
        <el-form-item label="Aff Bank Account">
          <el-popover v-if="formData.bankInfo" trigger="hover">
            <el-descriptions :column="1" border>
              <el-descriptions-item>
                <template slot="label">
                  Bank Name
                </template>
                {{ formData.bankInfo.bankName }}
              </el-descriptions-item>
              <el-descriptions-item>
                <template slot="label">
                  Bank Address
                </template>
                {{ formData.bankInfo.bankAddress }}
              </el-descriptions-item>
              <el-descriptions-item>
                <template slot="label">
                  Bank Account Number
                </template>
                {{ formData.bankInfo.bankAccountNumber }}
              </el-descriptions-item>
              <el-descriptions-item>
                <template slot="label">
                  Bank Account Name
                </template>
                {{ formData.bankInfo.bankAccountName }}
              </el-descriptions-item>
              <el-descriptions-item>
                <template slot="label">
                  Swift Code
                </template>
                {{ formData.bankInfo.swiftCode }}
              </el-descriptions-item>
              <el-descriptions-item>
                <template slot="label">
                  Branch Code
                </template>
                {{ formData.bankInfo.branchCode }}
              </el-descriptions-item>
            </el-descriptions>
            <el-link type="primary" slot="reference">{{
              formData.bankInfo.bankAccountName
            }}</el-link>
          </el-popover>
          <span v-else>N/A</span>
        </el-form-item>
        <el-form-item label="Send Emails">
          <div v-if="formData.sendEmail && formData.sendEmail.length > 0">
            <el-tag v-for="(email, index) in splitStr(formData.sendEmail, ';')" :key="index">{{
              email
            }}</el-tag>
          </div>
          <div v-else>
            N/A
          </div>
        </el-form-item>
        <el-form-item label="Payment Amount" prop="financePaymentAmount" required>
          <el-input v-model="formData.financePaymentAmount" type="number" />
        </el-form-item>
        <el-form-item label="Payment Time" prop="paymentTime" required>
          <el-date-picker
            v-model="formData.paymentTime"
            type="datetime"
            value-format="yyyy-MM-dd HH:mm:ss"
            placeholder="付款时间"
          >
          </el-date-picker>
        </el-form-item>
        <el-form-item label="Currency" prop="currency" required>
          <el-select v-model="formData.currency" filterable>
            <el-option
              v-for="item in currencyList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Payment Status" prop="paymentStatus">
          {{ showPaymentStatus(formData.paymentStatus) }}
        </el-form-item>
        <el-form-item label="Upload Receipt">
          <el-upload
            ref="upload"
            :multiple="true"
            list-type="file"
            :file-list="fileList"
            accept=".pdf"
            :action="S3Api.UPLOAD_FILE"
            :auto-upload="true"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :on-remove="handleRemoveInvoiceList"
            :before-upload="handleBeforeUploadInvoice"
            :on-progress="
              () => {
                formProcessing = true;
              }
            "
          >
            <el-button slot="trigger" type="primary" :disabled="formProcessing">上传文件</el-button>
            <div class="el-upload__tip" style="line-height: 0" slot="tip">文件大小不能超过100M</div>
          </el-upload>
        </el-form-item>
        <el-form-item v-if="dialogType === 'status'" label="Audit Remark" prop="remark">
          <el-input
            v-model="formData.financeAuditRemark"
            type="textarea"
            show-word-limit
            maxlength="255"
            placeholder="please input"
          />
        </el-form-item>
      </section>

      <!--   新增表单   -->
      <section v-if="dialogType === 'new'" class="grid-form">
        <el-form-item label="Date" prop="recordDateRange">
          <el-date-picker
            v-model="formData.recordDateRange"
            :disabled="paymentType !== 'POST_PAID'"
            format="yyyy-MM"
            value-format="yyyy-MM"
            type="monthrange"
            clearable
            align="right"
            unlink-panels
            style="width: 250px"
            range-separator="to"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
          />
        </el-form-item>
        <el-form-item label="Company Platform" prop="affCompanyPlatform" required>
          <el-select
            v-model="formData.affCompanyPlatform"
            filterable
            clearable
            @change="handleCompanyPlatformChange(formData.affCompanyPlatform)"
          >
            <el-option
              v-for="item in companyPlatformList"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Aff Entity" prop="affEntity" required>
          <el-select v-model="formData.affEntity" ref="companyEntityOptions" filterable clearable>
            <el-option
              v-for="item in specificCompanyEntity"
              :key="item.id"
              :label="item.companyEntity"
              :value="item.companyEntity"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="HyperTap Entity" prop="hyperTapEntity" required>
          <el-select v-model="formData.hyperTapEntity" filterable placeholder="please select">
            <el-option
              v-for="item in hyperTapCompanyEntities"
              :key="item.bfiId"
              :label="item.companyEntityAlias"
              :value="item.companyEntity"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Aff Confirmed Amount" prop="affConfirmedAmount">
          <el-input :value="previewData.totalAffConfirmedAmount" disabled />
        </el-form-item>
        <el-form-item label="Aff Hedge Amount" prop="affHedgeAmount">
          <el-input :value="previewData.totalAffHedgeAmount" disabled />
        </el-form-item>
        <el-form-item
          v-if="paymentType === 'POST_PAID'"
          label="Apply Payment Amount"
          prop="applyPaymentAmount"
        >
          <el-input v-model="previewData.applyPaymentAmount" disabled />
        </el-form-item>
        <el-form-item
          v-if="paymentType === 'PRE_PAID'"
          label="Apply Payment Amount"
          required
          prop="applyPaymentAmount"
        >
          <el-input v-model="formData.applyPaymentAmount" />
        </el-form-item>
        <el-form-item label="Currency" prop="currency" required>
          <el-select v-model="formData.currency" filterable placeholder="please select">
            <el-option
              v-for="item in currencyList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Aff Bank Account" prop="affBfbId" required>
          <el-select v-model="formData.affBfbId" filterable placeholder="please select">
            <el-option
              v-for="item in bankInfoList"
              :key="item.bankId"
              :label="item.bankAccountName"
              :value="item.bankId"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="paymentType === 'POST_PAID'"
          label="Payment Status"
          prop="paymentStatus"
          required
        >
          <el-select v-model="formData.paymentStatus" filterable placeholder="please select">
            <el-option
              v-for="item in affPaymentAppOperationPaymentStatusList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Upload Invoice" style="width: 500px">
          <el-upload
            ref="upload"
            :multiple="true"
            list-type="file"
            :file-list="fileList"
            accept=".pdf"
            :action="S3Api.UPLOAD_FILE"
            :auto-upload="true"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :on-remove="handleRemoveInvoiceList"
            :before-upload="handleBeforeUploadInvoice"
            :on-progress="
              () => {
                formProcessing = true;
              }
            "
          >
            <el-button slot="trigger" type="primary" :disabled="formProcessing">上传文件</el-button>
            <div class="el-upload__tip" style="line-height: 0" slot="tip">文件大小不能超过100M</div>
          </el-upload>
        </el-form-item>
        <el-form-item v-if="dialogType === 'new'" label="Remark" prop="remark">
          <el-input
            v-model="formData.remark"
            type="textarea"
            show-word-limit
            maxlength="255"
            placeholder="please input"
          />
        </el-form-item>
        <el-form-item v-if="dialogType === 'status'" label="Operation Audit Remark" prop="remark">
          <el-input
            v-model="formData.operationAuditRemark"
            type="textarea"
            show-word-limit
            maxlength="255"
            placeholder="please input"
          />
        </el-form-item>
      </section>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取 消</el-button>
      <el-button type="primary" :loading="formProcessing" @click="handleSubmit">确 定</el-button>
    </span>
  </el-dialog>
</template>

<style scoped lang="scss"></style>
